import React, { useEffect, useRef, useState } from "react";
import {
  Image,
  KeyboardAvoidingView,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as ImagePicker from "expo-image-picker";
import { Ionicons } from "@expo/vector-icons";
import { v4 as uuid } from "uuid";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import { getLanguage, isEnglish, t } from "@/localization";
import { useAuth } from "../AuthContext";
import { captureSupportScreen } from "./capture";
import TypingIndicator from "./TypingIndicator";
import { supportRequest } from "./api";
import {
  ONBOARDING_ROUTES,
  onOpenSupport,
  setSupportStatus,
  signInUrl,
  supportEnabled,
  supportPublic,
  useSupportRoute,
} from "./config";
import { embedParent, onParentMessage, postToParent } from "./embed";

const {
  messageParts,
  additionalSources,
  isSendShortcut,
  mergeSupportHistory,
  earlierItems,
  teamReplyCount,
  websiteSurface,
  isYouthStreamPath,
} = require("./message.cjs");
// The words message.cjs puts in the chat (speaker labels, unnamed files, dates),
// in the app's language.
const LABELS = {
  attachment: t("Attachment"),
  fin: t("Fin · previous AI assistant"),
  staff: t("EventLive support team"),
  staffNamed: (name) => t("{name} · EventLive support", { name }),
  assistant: t("EventLive AI assistant"),
  dateLocale: isEnglish() ? undefined : getLanguage(),
};
// Scope for visitors who aren't signed in (website and web sign-in screens).
const ANONYMOUS = "anonymous";
export default function SupportWidget(props) {
  return <SupportWidgetContent {...props} auth={useAuth()} />;
}
export function SupportWidgetContent({
  screenRef,
  navigationRef,
  auth,
  request: send_ = supportRequest,
  // Website chat: rendered full size inside an iframe on eventlive.pro or evt.live (/support-embed).
  embedded = false,
  allowAnonymous = supportPublic,
}) {
  const { width, height } = useWindowDimensions(),
    insets = useSafeAreaInsets(),
    isMobileUX = useIsMobileUX();
  const scope = auth.isLoading
    ? null
    : !auth.isSignedOut && (auth.broadcaster || auth.sub)
    ? auth.broadcaster || auth.sub
    : allowAnonymous
    ? ANONYMOUS
    : null;
  const anonymous = scope === ANONYMOUS;
  const request = (body, session, options = {}) =>
    send_(body, session, { ...options, anonymous });
  const parent = useRef(embedded ? embedParent() : null).current;
  const page = useRef(null);
  // A visitor on YouthStream's page (isYouthStreamPath), or whose chat began there: the answers
  // are YouthStream's, and so is the chat's wording.
  const [youthstreamPage, setYouthstreamPage] = useState(false),
    [youthstreamChat, setYouthstreamChat] = useState(false);
  // Signed-in broadcasters keep EventLive's answers wherever they are.
  const youthstream =
    embedded && anonymous && (youthstreamPage || youthstreamChat);
  const brand = youthstream ? "YouthStream" : "EventLive";
  // The iframe is only created when the visitor opens the website chat.
  const [open, setOpen] = useState(embedded),
    [expanded, setExpanded] = useState(false),
    [items, setItems] = useState([]),
    [earlier, setEarlier] = useState([]),
    [draft, setDraft] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [ready, setReady] = useState(false),
    // "ai", "human" (handed to the team, Luna still helps) or "staff" (team is answering).
    [teamState, setTeamState] = useState("ai"),
    [handoffNotice, setHandoffNotice] = useState(null),
    [answering, setAnswering] = useState(false),
    [photo, setPhoto] = useState(null),
    [unread, setUnread] = useState(false);
  const session = useRef(null),
    pending = useRef(null),
    epoch = useRef(0),
    scroll = useRef(null),
    opened = useRef(false),
    sending = useRef(false),
    activity = useRef(0),
    replies = useRef(0);
  const human = teamState !== "ai";
  const routeName = useSupportRoute();
  const storageKey = scope
    ? `eventlive.support.v1.${encodeURIComponent(scope)}`
    : null;
  useEffect(() => onOpenSupport(() => setOpen(true)), []);
  // Header buttons (SupportButton) open this chat and show its unread dot.
  const available = supportEnabled && !!scope && !embedded;
  useEffect(() => {
    setSupportStatus({ available, unread: available && unread });
  }, [available, unread]);
  useEffect(
    () => () => setSupportStatus({ available: false, unread: false }),
    []
  );
  useEffect(() => {
    opened.current = open;
    if (open) setUnread(false);
  }, [open]);
  useEffect(() => {
    if (!embedded) return;
    postToParent(parent, "ready");
    const stop = onParentMessage(parent, (message) => {
      if (typeof message.path === "string") {
        page.current = message.path.slice(0, 200);
        setYouthstreamPage(isYouthStreamPath(page.current));
      }
      if (message.action === "open") setOpen(true);
      if (message.action === "close") setOpen(false);
    });
    // Signing in on broadcaster.eventlive.pro in another tab: reload to use the account.
    const signedIn = (event) => {
      if (
        /^CognitoIdentityServiceProvider\..*\.LastAuthUser$/.test(
          event.key || ""
        )
      )
        window.location.reload();
    };
    // Focus is inside the iframe while chatting, so the page can't see Escape itself.
    // Capture phase: react-native-web's TextInput stops key events from bubbling.
    const escape = (event) => {
      if (event.key !== "Escape" || !parent) return;
      setOpen(false);
      postToParent(parent, "close");
    };
    window.addEventListener("storage", signedIn);
    window.addEventListener("keydown", escape, true);
    return () => {
      stop();
      window.removeEventListener("storage", signedIn);
      window.removeEventListener("keydown", escape, true);
    };
  }, [embedded, parent]);
  useEffect(() => {
    if (embedded) postToParent(parent, "unread", { count: unread ? 1 : 0 });
  }, [embedded, parent, unread]);
  useEffect(() => {
    const generation = ++epoch.current;
    session.current = null;
    pending.current = null;
    sending.current = false;
    setItems([]);
    setEarlier([]);
    setBusy(false);
    setAnswering(false);
    setTeamState("ai");
    replies.current = 0;
    setHandoffNotice(null);
    setError("");
    setPhoto(null);
    setDraft("");
    setReady(false);
    if (!scope || !supportEnabled) {
      // The website chat stays open while the sign-in state loads.
      if (!embedded) setOpen(false);
      return;
    }
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(storageKey);
        const saved = raw ? JSON.parse(raw) : null;
        if (epoch.current !== generation) return;
        if (saved) {
          session.current = saved;
          try {
            const history = await request(null, saved, {
              history: true,
              previous: !anonymous,
            });
            if (epoch.current !== generation) return;
            session.current = saved;
            setItems(
              history.messages.map((m, i) => ({
                id: `saved-${i}`,
                role: m.role,
                text: m.body,
                sources: m.sources,
              }))
            );
            setEarlier(earlierItems(history.previous, LABELS));
            setTeamState(history.state || "ai");
            replies.current = teamReplyCount(history.messages);
            setHandoffNotice(history.handoffNotice);
            return;
          } catch (e) {
            if (e.status !== 401 && e.status !== 403) throw e;
            await AsyncStorage.removeItem(storageKey);
            if (epoch.current === generation) session.current = null;
          }
        }
        // No saved chat on this device: still show the account's earlier
        // conversations (other devices and the previous Intercom/Fin chat).
        const prior = anonymous
          ? null
          : await request(null, null, { previous: true }).catch(() => null);
        if (prior && epoch.current === generation)
          setEarlier(earlierItems(prior.previous, LABELS));
      } catch (e) {
        if (epoch.current === generation)
          setError(
            t(
              "Could not restore your previous conversation. You can retry by reopening the app."
            )
          );
      } finally {
        if (epoch.current === generation) setReady(true);
      }
    })();
    return () => {
      epoch.current++;
    };
  }, [scope, storageKey]);
  // Once the team is involved, check for their replies: often while the chat is open,
  // occasionally while it's closed (to show the unread dot).
  useEffect(() => {
    if (!human || !ready) return;
    let cancelled = false;
    let fetching = false;
    const refresh = async () => {
      if (fetching || sending.current || !session.current) return;
      const generation = epoch.current;
      const revision = activity.current;
      const visible =
        opened.current &&
        (typeof document === "undefined" ||
          document.visibilityState === "visible");
      fetching = true;
      try {
        const history = await request(null, session.current, {
          history: true,
          seen: visible,
        });
        if (
          cancelled ||
          generation !== epoch.current ||
          revision !== activity.current ||
          sending.current
        )
          return;
        setItems((current) =>
          mergeSupportHistory(
            history.messages,
            pending.current,
            current,
            LABELS
          )
        );
        setTeamState(history.state || "ai");
        setHandoffNotice(history.handoffNotice);
        const count = teamReplyCount(history.messages);
        if (count > replies.current && !opened.current) setUnread(true);
        replies.current = count;
      } catch {
        /* Keep the current conversation visible during a connection failure. */
      } finally {
        fetching = false;
      }
    };
    if (open) refresh();
    const timer = setInterval(refresh, open ? 5000 : 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [open, human, ready, scope]);
  async function attach() {
    const generation = epoch.current;
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images"],
      base64: true,
      quality: 0.5,
      allowsEditing: true,
    });
    if (epoch.current !== generation) return;
    const image = result.assets?.[0];
    if (result.canceled || !image) return;
    if (!image.base64 || image.base64.length > 1499900) {
      setError(t("Please choose a smaller image."));
      return;
    }
    // Label the data by its own bytes: since Expo SDK 55 the iOS picker returns JPEG
    // base64 even when mimeType says image/png (screenshots), and the server rejects
    // a data URL whose type doesn't match its bytes ("Invalid image.").
    const type = image.base64.startsWith("iVBORw0KGgo")
      ? "image/png"
      : image.base64.startsWith("/9j/")
      ? "image/jpeg"
      : null;
    if (!type) {
      setError(t("Please choose a JPEG or PNG image."));
      return;
    }
    setPhoto(`data:${type};base64,${image.base64}`);
  }
  async function send(retry = false) {
    if (sending.current || !ready || (!retry && !draft.trim())) return;
    const generation = epoch.current;
    sending.current = true;
    activity.current++;
    setBusy(true);
    setError("");
    try {
      if (!retry) {
        const route = navigationRef?.current?.getCurrentRoute?.();
        const message = draft.trim(),
          requestId = uuid();
        if (embedded && isYouthStreamPath(page.current))
          setYouthstreamChat(true);
        setItems((old) => [
          ...old,
          { id: requestId, role: "user", text: message, photo },
        ]);
        setDraft("");
        pending.current = {
          requestId,
          message,
          eventSlug:
            typeof route?.params?.slug === "string"
              ? route.params.slug
              : undefined,
          screen: embedded
            ? {
                surface: websiteSurface(parent, page.current),
                platform: Platform.OS,
                appVersion: "website",
              }
            : {
                surface: route?.name || "broadcaster",
                platform: Platform.OS,
                appVersion: "broadcaster",
              },
          screenshot: anonymous
            ? null
            : photo ||
              (screenRef
                ? await captureSupportScreen(screenRef, route?.name)
                : null),
          // A photo they picked comes back with the chat; screenshots stay private.
          photo: !anonymous && !!photo,
        };
        if (epoch.current !== generation) return;
        setPhoto(null);
      }
      if (!session.current) {
        const created = await request({ initialize: true });
        if (epoch.current !== generation) return;
        session.current = created;
        await AsyncStorage.setItem(storageKey, JSON.stringify(created));
      }
      setAnswering(true);
      const response = await request(
        { ...pending.current, id: session.current.id },
        session.current
      );
      if (epoch.current !== generation) return;
      const answerId = `answer-${pending.current.requestId}`;
      // While the team is answering, Luna stays quiet (reply is null).
      if (response.reply)
        setItems((old) => [
          ...old,
          {
            id: answerId,
            role: "assistant",
            text: response.reply,
            sources: response.sources,
          },
        ]);
      pending.current = null;
      setTeamState((current) =>
        response.waiting
          ? "staff"
          : response.escalate
          ? current === "staff"
            ? "staff"
            : "human"
          : "ai"
      );
      if (response.handoffNotice) setHandoffNotice(response.handoffNotice);
      if (response.reply && !opened.current) setUnread(true);
    } catch (e) {
      if (epoch.current === generation) setError(e.message);
    } finally {
      if (epoch.current === generation) {
        sending.current = false;
        setBusy(false);
        setAnswering(false);
      }
    }
  }
  function renderBubble(item) {
    const label =
      item.label ||
      (item.role === "staff" ? t("{brand} support team", { brand }) : null);
    return (
      <View
        key={item.id}
        style={[
          item.role === "user" ? s.question : s.answer,
          item.earlier && s.earlier,
        ]}
      >
        {!!label && <Text style={s.hint}>{label}</Text>}
        <Text selectable style={s.text}>
          {messageParts(item.text).map((part, i) =>
            part.url ? (
              <Text
                key={i}
                accessibilityRole="link"
                style={s.link}
                onPress={() => Linking.openURL(part.url)}
              >
                {part.text}
              </Text>
            ) : (
              part.text
            )
          )}
        </Text>
        {item.photo && <Image source={{ uri: item.photo }} style={s.image} />}
        {(item.attachments || []).map((a) =>
          a.kind === "image" ? (
            <Pressable
              key={a.id}
              accessibilityRole="imagebutton"
              accessibilityLabel={t("Open {name}", { name: a.name })}
              onPress={() => Linking.openURL(a.url)}
            >
              <Image
                source={{ uri: a.url }}
                accessibilityLabel={a.name}
                style={s.image}
              />
            </Pressable>
          ) : (
            <Pressable
              key={a.id}
              accessibilityRole="link"
              onPress={() => Linking.openURL(a.url)}
              style={s.file}
            >
              <Ionicons
                name="document-attach-outline"
                size={18}
                color="#F84123"
              />
              <Text style={s.link}>{a.name}</Text>
            </Pressable>
          )
        )}
        {additionalSources(item.text, item.sources).map((x) => (
          <Pressable key={x.id} onPress={() => Linking.openURL(x.url)}>
            <Text style={s.link}>{x.title}</Text>
          </Pressable>
        ))}
      </View>
    );
  }
  if (!supportEnabled || !scope) return null;
  const close = () => {
    setOpen(false);
    postToParent(parent, "close");
  };
  const panel = (
    <View
      style={[
        s.panel,
        embedded
          ? s.embedded
          : {
              width: expanded ? width - 24 : Math.min(width - 24, 400),
              height: expanded
                ? height -
                  Math.max(insets.top, 12) -
                  Math.max(insets.bottom, 12)
                : Math.min(height - insets.top - insets.bottom - 28, 620),
            },
      ]}
    >
      <View style={s.header}>
        <View style={{ flex: 1 }}>
          <Text style={s.title}>{t("We’re here to help")}</Text>
          <Text style={s.subtitle}>
            {teamState === "staff"
              ? t("You’re chatting with the {brand} support team", { brand })
              : human
              ? t("Team review requested · AI can still help")
              : t("You’re talking with the {brand} AI assistant", { brand })}
          </Text>
        </View>
        {!embedded && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={
              expanded ? t("Collapse chat") : t("Expand chat")
            }
            accessibilityState={{ expanded }}
            onPress={() => setExpanded((value) => !value)}
            style={{ padding: 8, marginRight: 8 }}
          >
            <Ionicons
              name={expanded ? "contract-outline" : "expand-outline"}
              size={22}
              color="white"
            />
          </Pressable>
        )}
        {(!embedded || parent) && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("Close support")}
            onPress={embedded ? close : () => setOpen(false)}
            hitSlop={12}
          >
            <Ionicons name="close" size={24} color="white" />
          </Pressable>
        )}
      </View>
      <ScrollView
        ref={scroll}
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: 16, gap: 12 }}
        onContentSizeChange={() =>
          scroll.current?.scrollToEnd({ animated: true })
        }
        keyboardShouldPersistTaps="handled"
      >
        {earlier.map((item) =>
          item.role === "divider" ? (
            <Text key={item.id} style={s.divider}>
              {item.text}
            </Text>
          ) : (
            renderBubble(item)
          )
        )}
        {earlier.length > 0 && (
          <Text style={s.divider}>{t("Current conversation")}</Text>
        )}
        {!items.length && (
          <View style={s.answer}>
            {youthstream ? (
              <>
                <Text style={s.text}>
                  {t(
                    "Hi! Ask me anything about YouthStream: plans and pricing, streaming your team’s games, or watching a game."
                  )}
                </Text>
                <Text style={s.hint}>
                  {t(
                    "Using the app? For help with your account, open Support Chat in YouthStream’s Profile."
                  )}
                </Text>
              </>
            ) : anonymous ? (
              <>
                <Text style={s.text}>
                  {t(
                    "Hi! Ask me anything about EventLive: pricing, planning your livestream, or watching an event."
                  )}
                </Text>
                <Pressable onPress={() => Linking.openURL(signInUrl)}>
                  <Text style={s.link}>
                    {t("Have an account? Sign in for help with your events")}
                  </Text>
                </Pressable>
              </>
            ) : (
              <Text style={s.text}>
                {t(
                  "Ask anything about broadcasting, your events or pricing. I can use your account details and the screen you’re on to help."
                )}
              </Text>
            )}
          </View>
        )}
        {items.map(renderBubble)}
        {answering && <TypingIndicator />}
        {teamState === "staff" ? (
          <Text style={s.hint}>
            {t(
              "The {brand} team is answering here. If you step away, they’ll also reply by email.",
              { brand }
            )}
          </Text>
        ) : (
          human &&
          handoffNotice &&
          !String(items[items.length - 1]?.text || "").includes(
            handoffNotice
          ) && <Text style={s.hint}>{handoffNotice}</Text>
        )}
        {!!error && (
          <View accessibilityRole="alert">
            <Text style={s.error}>{error}</Text>
            {pending.current && (
              <Pressable onPress={() => send(true)} disabled={busy}>
                <Text style={s.link}>{t("Retry message")}</Text>
              </Pressable>
            )}
          </View>
        )}
      </ScrollView>
      <View style={s.composer}>
        {photo && (
          <View style={s.row}>
            <Image
              source={{ uri: photo }}
              style={{ width: 45, height: 45, borderRadius: 8 }}
            />
            <Pressable onPress={() => setPhoto(null)}>
              <Text style={s.link}>{t("Remove photo")}</Text>
            </Pressable>
          </View>
        )}
        <TextInput
          accessibilityLabel={t("Support message")}
          multiline
          maxLength={5000}
          value={draft}
          onChangeText={setDraft}
          onKeyPress={(event) => {
            if (isSendShortcut(event.nativeEvent || event)) {
              event.preventDefault?.();
              send();
            }
          }}
          placeholder={t("Tell us what you’re trying to do…")}
          style={s.input}
          editable={!busy && !pending.current}
        />
        <View style={s.row}>
          {!anonymous && (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("Attach")}
              onPress={() =>
                attach().catch(() => setError(t("Could not open your photos.")))
              }
              disabled={busy || !!pending.current}
              style={s.attach}
            >
              <Ionicons name="attach" size={23} color="#F84123" />
              <Text style={s.link}>{t("Attach")}</Text>
            </Pressable>
          )}
          <View style={{ flex: 1 }} />
          <Pressable
            accessibilityRole="button"
            disabled={!ready || busy || !draft.trim() || !!pending.current}
            onPress={() => send()}
            style={[
              s.send,
              (!ready || busy || !draft.trim() || !!pending.current) && {
                opacity: 0.4,
              },
            ]}
          >
            <Text style={{ color: "white", fontWeight: "600" }}>
              {t("Send")}
            </Text>
            <Ionicons name="send-outline" size={17} color="white" />
          </Pressable>
        </View>
      </View>
    </View>
  );
  if (embedded) return <View style={s.embeddedPage}>{panel}</View>;
  return (
    <>
      {/* The apps, and the web app's mobile layout once signed in, open the chat from the
          header (SupportButton), so nothing covers the screens' bottom buttons. Desktop web
          and signed-out visitors (no header button) keep the floating launcher. */}
      {!open &&
        Platform.OS === "web" &&
        !(isMobileUX && !anonymous) &&
        !ONBOARDING_ROUTES.has(routeName) && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("Open support")}
            onPress={() => setOpen(true)}
            style={[s.launcher, { bottom: Math.max(insets.bottom, 16) }]}
          >
            <Ionicons name="chatbubble-outline" size={20} color="#F84123" />
            <Text style={s.launchText}>{t("Help")}</Text>
            {unread && <View style={s.dot} />}
          </Pressable>
        )}
      <Modal
        visible={open}
        transparent
        animationType="fade"
        onRequestClose={() => setOpen(false)}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          style={[
            s.overlay,
            {
              paddingBottom: Math.max(insets.bottom, 12),
              paddingTop: Math.max(insets.top, 12),
            },
          ]}
        >
          <Pressable
            accessibilityLabel={t("Close support backdrop")}
            onPress={() => setOpen(false)}
            style={StyleSheet.absoluteFill}
          />
          {panel}
        </KeyboardAvoidingView>
      </Modal>
    </>
  );
}
const s = StyleSheet.create({
  launcher: {
    position: "absolute",
    right: 20,
    borderRadius: 28,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#E4D8D5",
    paddingHorizontal: 16,
    height: 46,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    elevation: 5,
    shadowColor: "#38211C",
    shadowOpacity: 0.15,
    shadowRadius: 10,
  },
  launchText: { color: "#F84123", fontSize: 15, fontWeight: "600" },
  dot: { width: 9, height: 9, backgroundColor: "#F84123", borderRadius: 5 },
  overlay: {
    flex: 1,
    alignItems: "flex-end",
    justifyContent: "flex-end",
    paddingHorizontal: 12,
    backgroundColor: "#211C1A22",
  },
  panel: {
    // Shrink with the window when the Android keyboard opens, keeping the header visible.
    flexShrink: 1,
    maxHeight: "100%",
    backgroundColor: "white",
    borderRadius: 24,
    overflow: "hidden",
    elevation: 12,
    shadowOpacity: 0.2,
    shadowRadius: 24,
  },
  embedded: { flex: 1, borderRadius: 0, elevation: 0, shadowOpacity: 0 },
  embeddedPage: { flex: 1, height: "100%", backgroundColor: "white" },
  header: {
    backgroundColor: "#F84123",
    padding: 20,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  title: { fontSize: 18, fontWeight: "600", color: "white" },
  subtitle: { fontSize: 12, color: "#FFF4F1", marginTop: 5 },
  question: {
    marginLeft: 24,
    backgroundColor: "#FFE7E1",
    borderRadius: 16,
    padding: 14,
  },
  answer: {
    marginRight: 16,
    backgroundColor: "#FFF5F2",
    borderRadius: 16,
    padding: 14,
  },
  text: { fontSize: 14, lineHeight: 21, color: "#342521" },
  hint: { fontSize: 11, lineHeight: 16, color: "#756963" },
  earlier: { opacity: 0.82 },
  divider: {
    fontSize: 11,
    color: "#8C7F79",
    textAlign: "center",
    marginVertical: 4,
  },
  link: {
    fontSize: 13,
    color: "#F84123",
    textDecorationLine: "underline",
    marginTop: 5,
  },
  composer: { borderTopWidth: 1, borderColor: "#EEE4E1", padding: 14, gap: 8 },
  row: { flexDirection: "row", alignItems: "center", gap: 8 },
  input: {
    minHeight: 64,
    maxHeight: 110,
    borderWidth: 1,
    borderColor: "#E4D8D5",
    borderRadius: 12,
    padding: 12,
    fontSize: 14,
    color: "#342521",
    textAlignVertical: "top",
  },
  send: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderRadius: 12,
    backgroundColor: "#F84123",
    paddingVertical: 11,
    paddingHorizontal: 18,
  },
  attach: { flexDirection: "row", alignItems: "center" },
  error: { color: "#a13f35", fontSize: 13 },
  image: { height: 140, width: "100%", resizeMode: "contain", marginTop: 8 },
  file: { flexDirection: "row", alignItems: "center", gap: 6, marginTop: 8 },
});
